import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const AREA_META = {
  overall:       { label: 'Overall',     color: '#c9a84c' },
  health:        { label: 'Health',      color: '#4caf7d' },
  mind:          { label: 'Mind',        color: '#9b5ce0' },
  wealth:        { label: 'Career',      color: '#e0a83c' },
  relationships: { label: 'Relations',   color: '#e05c5c' },
  faith:         { label: 'Faith',       color: '#5c8fe0' },
  purpose:       { label: 'Goals',     color: '#e07d3c' },
}

const FREE_RANGE = { label: '7D', days: 7 }
const PRO_RANGES = [
  { label: '1W',  days: 7 },
  { label: '1M',  days: 30 },
  { label: '6M',  days: 180 },
  { label: '1Y',  days: 365 },
  { label: 'ALL', days: 9999 },
]

export default function TrendsPage({ onNavigate }) {
  const { user, profile } = useAuth()
  const [history, setHistory] = useState([])
  const [selectedArea, setSelectedArea] = useState('overall')
  const [rangeIndex, setRangeIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const ranges = PRO_RANGES
  const currentRange = ranges[rangeIndex]

  useEffect(() => {
    if (user) loadHistory()
  }, [user, rangeIndex])

  async function loadHistory() {
    setLoading(true)

    // Get date range
    let startDate = null
    if (currentRange.days !== 9999) {
      const d = new Date()
      d.setDate(d.getDate() - currentRange.days)
      startDate = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
    }

    let query = supabase
      .from('manual_tasks')
      .select('date, area, final_points')
      .eq('user_id', user.id)
      .eq('flagged', false)
      .order('date', { ascending: true })

    if (startDate) query = query.gte('date', startDate)

    const { data } = await query

    if (!data || data.length === 0) { setHistory([]); setLoading(false); return }

    // Group by date and calculate scores per area
    const byDate = {}
    data.forEach(t => {
      if (!byDate[t.date]) byDate[t.date] = { date: t.date, health: 0, mind: 0, career: 0, relationships: 0, faith: 0, purpose: 0 }
      if (byDate[t.date][t.area] !== undefined) byDate[t.date][t.area] += t.final_points
    })

    // Convert to array and add overall score
    const rows = Object.values(byDate).map(d => {
      const areas = [d.health, d.mind, d.career, d.relationships, d.faith, d.purpose]
      const overall = Math.round(areas.reduce((a, b) => a + b, 0) / 6)
      return {
        date: d.date,
        overall_score: Math.min(100, overall),
        health_score: Math.min(100, d.health),
        mind_score: Math.min(100, d.mind),
        career_score: Math.min(100, d.career),
        relationships_score: Math.min(100, d.relationships),
        faith_score: Math.min(100, d.faith),
        purpose_score: Math.min(100, d.purpose),
      }
    })

    setHistory(rows)
    setLoading(false)
  }

  const scoreKey = selectedArea === 'overall' ? 'overall_score' : `${selectedArea}_score`
  const scores = history.map(h => h[scoreKey] || 0)
  const maxScore = Math.max(...scores, 100)
  const minScore = Math.max(0, Math.min(...scores, 0) - 10)
  const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0
  const trend = scores.length >= 2 ? scores[scores.length - 1] - scores[0] : 0
  const meta = AREA_META[selectedArea]

  function formatDate(dateStr) {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return `${d.getMonth() + 1}/${d.getDate()}`
  }

  // Only show a subset of date labels to avoid crowding
  function showLabel(i, total) {
    if (total <= 7) return true
    if (total <= 14) return i % 2 === 0
    if (total <= 30) return i % 5 === 0 || i === total - 1
    return i % Math.ceil(total / 6) === 0 || i === total - 1
  }

  // Build SVG line chart path
  const chartW = 340
  const chartH = 100
  const pad = 8

  function getX(i) {
    if (scores.length <= 1) return chartW / 2
    return pad + (i / (scores.length - 1)) * (chartW - pad * 2)
  }

  function getY(val) {
    return chartH - pad - ((val - minScore) / (maxScore - minScore)) * (chartH - pad * 2)
  }

  const linePath = scores.length > 1
    ? scores.map((v, i) => `${i === 0 ? 'M' : 'L'}${getX(i).toFixed(1)},${getY(v).toFixed(1)}`).join(' ')
    : ''

  const areaPath = scores.length > 1
    ? `${linePath} L${getX(scores.length - 1).toFixed(1)},${chartH} L${getX(0).toFixed(1)},${chartH} Z`
    : ''

  return (
    <div style={s.page}>
      <div style={s.ambient} />

      <div style={s.header}>
        <div style={s.backBtn} onClick={() => onNavigate('dashboard')}>←</div>
        <div style={s.headerTitle}>Trends</div>
        <div style={{ width: 36 }} />
      </div>

      <div style={s.main}>

        {/* SUMMARY CARDS */}
        <div style={s.summaryRow}>
          <div style={s.summaryCard}>
            <div style={s.summaryLabel}>Avg Score</div>
            <div style={s.summaryValue}>{avgScore}</div>
          </div>
          <div style={s.summaryCard}>
            <div style={s.summaryLabel}>Trend</div>
            <div style={{ ...s.summaryValue, color: trend >= 0 ? '#4caf7d' : '#e05c5c' }}>
              {trend >= 0 ? '+' : ''}{trend}
            </div>
          </div>
        </div>

        {/* AREA SELECTOR */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Life Area</div>
          <div style={s.areaScroll}>
            {Object.entries(AREA_META).map(([key, val]) => (
              <div
                key={key}
                style={{
                  ...s.areaChip,
                  borderColor: selectedArea === key ? val.color : 'rgba(255,255,255,0.13)',
                  color: selectedArea === key ? val.color : '#6b6860',
                  background: selectedArea === key ? `${val.color}12` : 'transparent',
                }}
                onClick={() => setSelectedArea(key)}
              >
                {val.label}
              </div>
            ))}
          </div>
        </div>

        {/* CHART */}
        <div style={s.section}>
          <div style={s.card}>
            <div style={s.chartHeader}>
              <div>
                <div style={{ ...s.chartTitle, color: meta.color }}>{meta.label}</div>
                <div style={s.chartSub}>
                  {currentRange.label === 'ALL' ? 'All time' : `Last ${currentRange.label}`} · avg {avgScore}
                </div>
              </div>
              <div style={{ ...s.trendBadge, background: trend >= 0 ? 'rgba(76,175,125,0.12)' : 'rgba(224,92,92,0.12)', color: trend >= 0 ? '#4caf7d' : '#e05c5c', border: `1px solid ${trend >= 0 ? 'rgba(76,175,125,0.25)' : 'rgba(224,92,92,0.25)'}` }}>
                {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}
              </div>
            </div>

            {loading ? (
              <div style={s.empty}>Loading...</div>
            ) : history.length === 0 ? (
              <div style={s.empty}>No data yet. Start checking in daily!</div>
            ) : history.length === 1 ? (
              <div style={s.empty}>Check in again tomorrow to see your trend.</div>
            ) : (
              <>
                {/* SVG LINE CHART */}
                <div style={s.chartWrap}>
                  {/* Y axis grid lines */}
                  {[25, 50, 75, 100].map(v => (
                    <div key={v} style={{ ...s.gridLine, bottom: `${v}%` }}>
                      <span style={s.gridLabel}>{v}</span>
                    </div>
                  ))}

                  <svg
                    viewBox={`0 0 ${chartW} ${chartH}`}
                    style={{ width: '100%', height: '120px', overflow: 'visible' }}
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <linearGradient id={`areaGrad-${selectedArea}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={meta.color} stopOpacity="0.25" />
                        <stop offset="100%" stopColor={meta.color} stopOpacity="0.02" />
                      </linearGradient>
                    </defs>

                    {/* Area fill */}
                    <path
                      d={areaPath}
                      fill={`url(#areaGrad-${selectedArea})`}
                    />

                    {/* Line */}
                    <path
                      d={linePath}
                      fill="none"
                      stroke={meta.color}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    {/* Dots - only show for small datasets */}
                    {scores.length <= 14 && scores.map((v, i) => (
                      <circle
                        key={i}
                        cx={getX(i)}
                        cy={getY(v)}
                        r="3"
                        fill={meta.color}
                        opacity="0.9"
                      />
                    ))}

                    {/* Last point highlight */}
                    <circle
                      cx={getX(scores.length - 1)}
                      cy={getY(scores[scores.length - 1])}
                      r="4"
                      fill={meta.color}
                      style={{ filter: `drop-shadow(0 0 4px ${meta.color})` }}
                    />
                  </svg>

                  {/* X axis labels */}
                  <div style={s.xAxis}>
                    {history.map((entry, i) => (
                      <div key={entry.date} style={{ flex: 1, textAlign: 'center' }}>
                        {showLabel(i, history.length) && (
                          <span style={s.xLabel}>{formatDate(entry.date)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* TIME RANGE */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Time Range</div>
          <div style={s.rangeRow}>
            {PRO_RANGES.map((r, i) => (
              <div
                key={r.label}
                style={{
                  ...s.rangeChip,
                  background: rangeIndex === i ? '#c9a84c' : 'rgba(255,255,255,0.05)',
                  color: rangeIndex === i ? '#000' : '#6b6860',
                  fontWeight: rangeIndex === i ? '700' : '400',
                }}
                onClick={() => setRangeIndex(i)}
              >
                {r.label}
              </div>
            ))}
          </div>
        </div>

        {/* HIGHLIGHTS */}
        {scores.length >= 3 && (
          <div style={s.section}>
            <div style={s.sectionTitle}>Highlights</div>
            <div style={s.highlightRow}>
              <div style={s.highlightCard}>
                <div style={s.highlightLabel}>Best Day</div>
                <div style={{ ...s.highlightVal, color: '#4caf7d' }}>{Math.max(...scores)}</div>
                <div style={s.highlightDate}>{formatDate(history[scores.indexOf(Math.max(...scores))]?.date)}</div>
              </div>
              <div style={s.highlightCard}>
                <div style={s.highlightLabel}>Lowest Day</div>
                <div style={{ ...s.highlightVal, color: '#e05c5c' }}>{Math.min(...scores)}</div>
                <div style={s.highlightDate}>{formatDate(history[scores.indexOf(Math.min(...scores))]?.date)}</div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

const s = {
  page: { minHeight: '100vh', background: '#111118', color: '#e8e6df', fontFamily: "'DM Sans', sans-serif", position: 'relative', overflowX: 'hidden' },
  ambient: { position: 'fixed', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'radial-gradient(ellipse, rgba(201,168,76,0.22) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  header: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', background: 'rgba(6,6,8,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.12)' },
  backBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', cursor: 'pointer', fontSize: '18px', color: '#c9a84c' },
  headerTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px', color: '#e8e6df' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '24px 20px 100px', position: 'relative', zIndex: 1 },
  summaryRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '28px' },
  summaryCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '16px 12px', textAlign: 'center' },
  summaryLabel: { fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '6px' },
  summaryValue: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', color: '#f0cc7a', letterSpacing: '1px', lineHeight: 1 },
  section: { marginBottom: '24px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '12px' },
  areaScroll: { display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px', scrollbarWidth: 'none' },
  areaChip: { padding: '7px 14px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s', border: '1px solid', letterSpacing: '0.5px' },
  card: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '20px' },
  chartHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  chartTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '2px', lineHeight: 1 },
  chartSub: { fontSize: '11px', color: '#6b6860', marginTop: '4px' },
  trendBadge: { fontSize: '12px', fontWeight: '600', padding: '5px 10px', borderRadius: '8px' },
  chartWrap: { position: 'relative', paddingLeft: '28px' },
  gridLine: { position: 'absolute', left: '28px', right: 0, height: '1px', background: 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center' },
  gridLabel: { position: 'absolute', left: '-28px', fontSize: '9px', color: 'rgba(255,255,255,0.2)', width: '22px', textAlign: 'right' },
  xAxis: { display: 'flex', marginTop: '6px' },
  xLabel: { fontSize: '9px', color: '#6b6860' },
  empty: { textAlign: 'center', color: '#6b6860', fontSize: '14px', padding: '30px 0' },
  rangeRow: { display: 'flex', gap: '8px' },
  rangeChip: { flex: 1, padding: '11px 4px', borderRadius: '10px', fontSize: '12px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', letterSpacing: '0.5px' },
  proGate: { background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '14px', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '8px' },
  proGateText: { fontSize: '13px', color: '#e8e6df' },
  proGatePricing: { fontSize: '12px', color: '#c9a84c', fontWeight: '500' },
  proGateBtn: { fontSize: '12px', color: '#000', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', textAlign: 'center' },
  highlightRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  highlightCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '18px', textAlign: 'center' },
  highlightLabel: { fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '8px' },
  highlightVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '40px', lineHeight: 1 },
  highlightDate: { fontSize: '11px', color: '#6b6860', marginTop: '6px' },
}
